import React, { useEffect, useState } from "react";
import {
  getData,
  postData,
  putData,
  deleteData,
  patchData
} from "../api/ApiServices";
import TableComponent from "./TableComponent";
import Pagination from "../view/common/pagination/Pagination";
import { Plus } from "lucide-react";
import CustomAlert from "../view/common/alert/CustomAlert";
import "../styles/common/sub-menu.css";
import "../styles/common/spinner.css";
import "../styles/common/icon-style.css";
import "../styles/common/toplayout.css";
import "../styles/common/btn.css";
import "../styles/common/custom-alert.css";
import "../styles/ItemModels.css";

import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/slices/store";
import { resetFilters, updateFilters } from "../redux/slices/filterSlice";
import { Role } from '../models/roles';
import RoleModal from "../view/common/RoleModel/RoleModel";

const Roles: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [currentRole, setCurrentRole] = useState<Role>({
    userType: "",
    status: "ACTIVE",
    menus: {}
  });
  const [editMode, setEditMode] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const [validationErrors, setValidationErrors] = useState<{
    userType?: string;
    status?: string;
  }>({});

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [sortBy, setSortBy] = useState<string>("userType");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertConfirmAction, setAlertConfirmAction] = useState<() => void>(
    () => { }
  );
  const [alertCancelAction, setAlertCancelAction] = useState<() => void>(
    () => { }
  );
  const [showConfirmButton, setShowConfirmButton] = useState(true);
  const [showCancelButton, setShowCancelButton] = useState(true);
  const [confirmButtonText, setConfirmButtonText] = useState("OK");
  const [cancelButtonText, setCancelButtonText] = useState("Cancel");
  const [autoCloseDelay, setAutoCloseDelay] = useState<number | null>(null);

  const excludeFieldsForRoles = ["menus", "createdBy", "updatedBy"];

  const dispatch = useDispatch();
  const filters = useSelector((state: RootState) => state.filters);
  const permission = useSelector((state: RootState) => state.permissions);

  const [initializeRoles, isRolesInitialized] = useState(false);

  useEffect(() => {
    dispatch(resetFilters());
    isRolesInitialized(true);
  }, [dispatch]);

  useEffect(() => {
    if (initializeRoles) {
      loadData();
    }
  }, [
    initializeRoles,
    currentPage,
    itemsPerPage,
    searchTerm,
    filters,
    sortBy,
    sortOrder,
  ]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const result = await getData<any>("/roles", {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        filter: filters,
        sortBy: sortBy,
        sortOrder: sortOrder,
      });

      if (result && result.data) {
        setRoles(result.data.data || []);
        if (result.data.pagination) {
          setTotalItems(result.data.pagination.total ?? 0);
          setTotalPages(result.data.pagination.totalPages ?? 1);
        }
      }
    } catch (err) {
      setError("Failed to load roles");
      console.error("Error loading roles:", err);
    }
    setIsLoading(false);
  };

  const closeAlert = () => {
    setShowAlert(false);
  };

  const configureAlert = (
    message: string,
    confirmAction: () => void,
    cancelAction: () => void = () => { },
    confirmText: string = "OK",
    cancelText: string = "Cancel",
    showConfirm: boolean = true,
    showCancel: boolean = true,
    autoClose: number | null = null
  ) => {
    setAlertMessage(message);
    setAlertConfirmAction(() => confirmAction);
    setAlertCancelAction(() => cancelAction);
    setConfirmButtonText(confirmText);
    setCancelButtonText(cancelText);
    setShowConfirmButton(showConfirm);
    setShowCancelButton(showCancel);
    setAutoCloseDelay(autoClose);
    setShowAlert(true);
  };

  const checkForDuplicates = (): boolean => {
    const existingRoles = editMode
      ? roles.filter((role) => role._id !== currentRole._id)
      : roles;

    const duplicateUserType = existingRoles.find(
      (role) => role.userType.toLowerCase() === currentRole.userType.toLowerCase()
    );

    const errors: { userType?: string } = { ...validationErrors };

    if (duplicateUserType) {
      errors.userType = "User Type already exists";
    }

    setValidationErrors(errors);

    if (duplicateUserType) {
      configureAlert(
        "User Type already exists!",
        closeAlert,
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000
      );
      return true;
    }

    return false;
  };

const validateForm = (): boolean => {
    const errors: { userType?: string; status?: string } = {};

    if (!currentRole.userType.trim()) {
      errors.userType = "User Type is required";
    }

    if (editMode && !currentRole.status) {
      errors.status = "Status is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const hasChanges = (): boolean => {
    if (!editMode) return true;

    const originalRole = roles.find(role => role._id === currentRole._id);
    if (!originalRole) return true;
    if (
      originalRole.userType !== currentRole.userType ||
      originalRole.status !== currentRole.status
    ) {
      return true;
    }

    const originalMenus = originalRole.menus || {};
    const currentMenus = currentRole.menus || {};
    const originalMenuNames = Object.keys(originalMenus);
    const currentMenuNames = Object.keys(currentMenus);
    const allMenuNamesMap: Record<string, boolean> = {};
    originalMenuNames.forEach(name => {
      allMenuNamesMap[name] = true;
    });

    currentMenuNames.forEach(name => {
      allMenuNamesMap[name] = true;
    });

    const allMenuNames = Object.keys(allMenuNamesMap);

    for (const menuName of allMenuNames) {
      const original = originalMenus[menuName] || {};
      const current = currentMenus[menuName] || {};

      if (
        original.create !== current.create ||
        original.read !== current.read ||
        original.update !== current.update ||
        original.delete !== current.delete
      ) {
        return true;
      }
    }

    return false;
  };

  const handleSortChange = (
    sortByField: string,
    sortOrderDirection: "asc" | "desc"
  ) => {
    setSortBy(sortByField);
    setSortOrder(sortOrderDirection);
  };

  const handleAddRole = async () => {
    setIsLoading(true);
    try {
      const result = await getData<any>("/roles");

      let newRoleMenus = {};

      if (result && result.data && result.data.data && result.data.data.length > 0) {
        const templateMenus = result.data.data[0].menus || {};
        newRoleMenus = Object.keys(templateMenus).reduce((acc, menuName) => {
          acc[menuName] = {
            name: menuName,
            create: false,
            read: false,
            update: false,
            delete: false
          };
          return acc;
        }, {} as Record<string, any>);
      }

      setCurrentRole({
        userType: "",
        status: "ACTIVE",
        menus: newRoleMenus
      });
      setValidationErrors({});
      setEditMode(false);
      setIsModalOpen(true);
    } catch (error) {
      console.error("Error fetching menus:", error);
      configureAlert(
        "Failed to load menus.",
        closeAlert,
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000
      );
    }
    setIsLoading(false);
  };

  const handleEdit = (role: Role) => {
    const roleCopy = JSON.parse(JSON.stringify(role));
    setCurrentRole(roleCopy);
    setValidationErrors({});
    setEditMode(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    const roleToDelete = roles.find((role) => role._id === id);

    if (roleToDelete) {
      configureAlert(
        `Are you sure you want to Inactive "${roleToDelete.userType}"?`,
        async () => {
          closeAlert();

          try {
            await deleteData("/roles", id);

            configureAlert(
              "Role has been Inactive successfully.",
              () => {
                loadData();
                closeAlert();
              },
              closeAlert,
              "OK",
              "",
              true,
              false,
              3000
            );
          } catch (error) {
            console.error("Error deleting role:", error);
            configureAlert(
              "Failed to delete role.",
              closeAlert,
              closeAlert,
              "OK",
              "",
              true,
              false,
              3000
            );
          }
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null
      );
    }
  };

  const handlePermissionChange = (menuName: string, permission: string, checked: boolean) => {
    setCurrentRole(prevRole => {
      const updatedRole = JSON.parse(JSON.stringify(prevRole));
      if (!updatedRole.menus[menuName]) {
        updatedRole.menus[menuName] = {
          name: menuName,
          create: false,
          read: false,
          update: false,
          delete: false
        };
      }
      updatedRole.menus[menuName][permission] = checked;
      return updatedRole;
    });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const hasPermissions = Object.keys(currentRole.menus || {}).some(menuName => {
      const menu = currentRole.menus[menuName];
      return menu?.create || menu?.read || menu?.update || menu?.delete;
    });

    if (!hasPermissions) {
      configureAlert(
        "Please select at least one permission to continue.",
        closeAlert,
        closeAlert,
        "OK",
        "",
        true,
        false,
        null
      );
      return;
    }

    if (!validateForm()) {
      return;
    }

    if (checkForDuplicates()) {
      return;
    }

    if (editMode && !hasChanges()) {
      setIsModalOpen(false);
      configureAlert(
        "No changes were made.",
        closeAlert,
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000
      );
      return;
    }

    setIsLoading(true);
    try {
      const updatedRole = JSON.parse(JSON.stringify(currentRole));
      if (updatedRole.menus) {
        Object.keys(updatedRole.menus).forEach(menuKey => {
          updatedRole.menus[menuKey].name = menuKey;
          updatedRole.menus[menuKey].create = Boolean(updatedRole.menus[menuKey].create);
          updatedRole.menus[menuKey].read = Boolean(updatedRole.menus[menuKey].read);
          updatedRole.menus[menuKey].update = Boolean(updatedRole.menus[menuKey].update);
          updatedRole.menus[menuKey].delete = Boolean(updatedRole.menus[menuKey].delete);
        });
      }
      if (!editMode) {
        delete updatedRole.status;
      }

      if (editMode) {
        await patchData<any>("/roles", currentRole._id as string, updatedRole);
        setIsModalOpen(false);
        configureAlert(
          `"${currentRole.userType}" has been updated successfully.`,
          () => {
            loadData();
            closeAlert();
          },
          closeAlert,
          "OK",
          "",
          true,
          false,
          3000
        );
      } else {
        const response = await postData<any>("/roles", updatedRole);
        setIsModalOpen(false);
        configureAlert(
          `"${currentRole.userType}" has been added successfully.`,
          () => {
            loadData();
            closeAlert();
          },
          closeAlert,
          "OK",
          "",
          true,
          false,
          3000
        );
      }
    } catch (error) {
      console.error("Error saving role:", error);
      configureAlert(
        `Failed to ${editMode ? "update" : "add"} role.`,
        closeAlert,
        closeAlert,
        "OK",
        "",
        true,
        false,
        3000
      );
    }
    setIsLoading(false);
  };

  const handleModalClose = () => {
    if (
      isModalOpen &&
      (editMode || currentRole.userType.trim() !== "")
    ) {
      configureAlert(
        "Are you sure you want to close without saving?",
        () => {
          setIsModalOpen(false);
          closeAlert();
        },
        closeAlert,
        "Yes",
        "No",
        true,
        true,
        null
      );
    } else {
      setIsModalOpen(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentRole((prev: Role) => ({
      ...prev,
      [name]: value,
    }));

    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSelectChange = (selectedOption: any, { name }: any) => {
    setCurrentRole((prev: Role) => ({
      ...prev,
      [name]: selectedOption?.value || "",
    }));

    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const columns = [
    { key: "userType", label: "Name", sortable: true },
    { key: "status", label: "Status", sortable: true },
  ];

  return (
    <div className="item-models-container">
      <div className="item-models-header">
        <div className="border">
          <div className="start-end-layout">
            <div className="breadcrumb-nav">
              <span>User Management</span>
              <span>Roles</span>
            </div>
            {permission?.menus?.roles?.create && (
              <div className="items-button-container tooltip-wrapper">
                <button className="item-models-add-btn" onClick={handleAddRole}>
                  <Plus size={16} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="box-container">
        {error && <div className="error-message">{error}</div>}

        {isLoading ? (
          <div className="spinner"></div>
        ) : (
          <>
            <TableComponent
              columns={columns}
              data={roles}
              onEdit={handleEdit}
              onDelete={handleDelete}
              setFilter={(filters: Record<string, any>) => {
                Object.entries(filters).forEach(([key, value]) => {
                  dispatch(updateFilters({ key, value: String(value) }));
                });
              }}
              setSorting={handleSortChange}
              defaultSortConfig={{ key: sortBy, direction: sortOrder }}
              excludeFields={excludeFieldsForRoles}
              context="roles"
            />

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              totalItems={totalItems}
            />
          </>
        )}
      </div>

      <RoleModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSave={handleSave}
        currentRole={currentRole}
        handleInputChange={handleInputChange}
        handleSelectChange={handleSelectChange}
        handlePermissionChange={handlePermissionChange}
        validationErrors={validationErrors}
        editMode={editMode}
        isLoading={isLoading}
      />

      {showAlert && (
        <CustomAlert
          message={alertMessage}
          onConfirm={alertConfirmAction}
          onCancel={alertCancelAction}
          confirmText={confirmButtonText}
          cancelText={cancelButtonText}
          showConfirmButton={showConfirmButton}
          showCancelButton={showCancelButton}
          autoCloseDelay={autoCloseDelay}
        />
      )}
    </div>
  );
};

export default Roles;